import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../lib/api';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { FileSpreadsheet, FileDown, Plus, X, GripVertical, Save, FolderOpen, Trash2, Play, Loader2, ArrowUpDown, Filter, Download, BarChart3, Database, TableIcon, PieChart } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import AppShell from '../components/AppShell';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6', '#e11d48', '#84cc16'];

// ─── Sortable Column Chip ───
function SortableChip({ id, label, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border bg-card text-sm select-none group" data-testid={`selected-col-${id}`}>
      <GripVertical className="w-3 h-3 text-muted-foreground cursor-grab" {...attributes} {...listeners} />
      <span className="truncate max-w-[140px]">{label}</span>
      <button onClick={() => onRemove(id)} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Available Field Item ───
function FieldItem({ fieldKey, field, selected, onToggle }) {
  const typeColors = { string: 'bg-blue-500/10 text-blue-500', number: 'bg-emerald-500/10 text-emerald-500', date: 'bg-amber-500/10 text-amber-500', boolean: 'bg-purple-500/10 text-purple-500' };
  return (
    <button
      onClick={() => onToggle(fieldKey)}
      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors text-left ${selected ? 'bg-[hsl(var(--brand))]/10 border border-[hsl(var(--brand))]/30' : 'hover:bg-accent/50 border border-transparent'}`}
      data-testid={`field-${fieldKey}`}
    >
      <span className={selected ? 'font-medium' : ''}>{field.label}</span>
      <Badge variant="outline" className={`text-[10px] px-1.5 ${typeColors[field.type] || ''}`}>{field.type}</Badge>
    </button>
  );
}

export default function ReportBuilderPage() {
  const [schema, setSchema] = useState({});
  const [source, setSource] = useState('');
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [page, setPage] = useState(0);
  const [savedReports, setSavedReports] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [reportName, setReportName] = useState('');
  const [editingReportId, setEditingReportId] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'chart'
  const [chartType, setChartType] = useState('bar'); // bar, line, pie, area
  const [chartXField, setChartXField] = useState('');
  const [chartYField, setChartYField] = useState('');
  const PAGE_SIZE = 50;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    reportsAPI.getSchema().then(res => {
      setSchema(res.data.sources || {});
      const keys = Object.keys(res.data.sources || {});
      if (keys.length > 0) setSource(keys[0]);
      setSchemaLoading(false);
    }).catch(() => setSchemaLoading(false));
    reportsAPI.listSaved().then(res => setSavedReports(res.data || [])).catch(() => {});
  }, []);

  // Reset columns when source changes
  useEffect(() => {
    if (source && schema[source]) {
      const fieldKeys = Object.keys(schema[source].fields);
      setColumns(fieldKeys.slice(0, 6));
      setFilters({});
      setSortField('');
      setRows([]);
      setTotal(0);
      setPage(0);
    }
  }, [source, schema]);

  const currentSource = schema[source] || {};
  const allFields = currentSource.fields || {};
  const filterableFields = (currentSource.filters || []);

  const toggleColumn = (key) => {
    setColumns(prev => prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]);
  };

  const removeColumn = (key) => setColumns(prev => prev.filter(c => c !== key));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setColumns(prev => {
        const oldIndex = prev.indexOf(active.id);
        const newIndex = prev.indexOf(over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const runReport = useCallback(async (pageOverride) => {
    if (!source || columns.length === 0) return;
    setLoading(true);
    const p = pageOverride ?? page;
    try {
      const res = await reportsAPI.execute({
        source, columns, filters,
        sort_field: sortField || undefined,
        sort_dir: sortDir,
        limit: PAGE_SIZE, offset: p * PAGE_SIZE,
      });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run report');
    }
    setLoading(false);
  }, [source, columns, filters, sortField, sortDir, page]);

  const handleExport = async (format) => {
    if (!source || columns.length === 0) return;
    setExporting(format);
    try {
      const exportFn = format === 'csv' ? reportsAPI.exportCSV : reportsAPI.exportExcel;
      const res = await exportFn({
        source, columns, filters,
        sort_field: sortField || undefined, sort_dir: sortDir,
        limit: 5000,
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${source}_${new Date().toISOString().slice(0, 10)}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
    setExporting(null);
  };

  const handleSave = async () => {
    if (!reportName.trim()) return;
    try {
      const payload = { name: reportName, source, columns, filters, sort_field: sortField, sort_dir: sortDir };
      if (editingReportId) {
        await reportsAPI.updateSaved(editingReportId, payload);
        toast.success('Report updated');
      } else {
        await reportsAPI.saveReport(payload);
        toast.success('Report saved');
      }
      const res = await reportsAPI.listSaved();
      setSavedReports(res.data || []);
      setShowSaveDialog(false);
      setReportName('');
      setEditingReportId(null);
    } catch (e) { toast.error('Failed to save report'); }
  };

  const handleLoad = (report) => {
    setSource(report.source);
    setTimeout(() => {
      setColumns(report.columns || []);
      setFilters(report.filters || {});
      setSortField(report.sort_field || '');
      setSortDir(report.sort_dir || 'asc');
      setEditingReportId(report.id);
      setReportName(report.name);
      setShowLoadDialog(false);
      setPage(0);
      setRows([]);
    }, 100);
    toast.success(`Loaded: ${report.name}`);
  };

  const handleDelete = async (id) => {
    try {
      await reportsAPI.deleteSaved(id);
      setSavedReports(prev => prev.filter(r => r.id !== id));
      toast.success('Report deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    runReport(newPage);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (schemaLoading) {
    return <AppShell><div className="flex items-center justify-center h-[calc(100vh-4rem)]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="report-builder">
        {/* Top Bar */}
        <div className="border-b p-4 flex items-center justify-between bg-card">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-[hsl(var(--brand))]" />
            <div>
              <h1 className="text-lg font-semibold" style={{ fontFamily: 'DM Sans' }}>Report Builder</h1>
              <p className="text-xs text-muted-foreground">Select a data source, choose columns, add filters, and run your report</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowLoadDialog(true)} data-testid="load-report-btn">
              <FolderOpen className="w-4 h-4 mr-1" />Saved Reports
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setShowSaveDialog(true); if (!reportName) setReportName(''); }} data-testid="save-report-btn">
              <Save className="w-4 h-4 mr-1" />Save
            </Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel: Source & Fields */}
          <div className="w-64 border-r flex flex-col bg-card/50">
            <div className="p-3 border-b">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Data Source</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="h-9" data-testid="source-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(schema).map(([key, src]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Database className="w-3.5 h-3.5" />
                        {src.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 border-b">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Available Fields</p>
              <p className="text-[10px] text-muted-foreground mb-2">Click to add/remove from report</p>
            </div>
            <ScrollArea className="flex-1 p-2">
              <div className="space-y-1">
                {Object.entries(allFields).map(([key, field]) => (
                  <FieldItem key={key} fieldKey={key} field={field} selected={columns.includes(key)} onToggle={toggleColumn} />
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Main Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Selected Columns Row */}
            <div className="p-3 border-b bg-card/30">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs text-muted-foreground font-medium">Report Columns</p>
                <Badge variant="outline" className="text-[10px]">{columns.length} selected</Badge>
                <span className="text-[10px] text-muted-foreground">Drag to reorder</span>
              </div>
              {columns.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Click fields on the left to add columns</p>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={columns} strategy={horizontalListSortingStrategy}>
                    <div className="flex flex-wrap gap-1.5" data-testid="selected-columns">
                      {columns.map(col => (
                        <SortableChip key={col} id={col} label={allFields[col]?.label || col} onRemove={removeColumn} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>

            {/* Filters Row */}
            {filterableFields.length > 0 && (
              <div className="p-3 border-b bg-card/20">
                <div className="flex items-center gap-2 mb-2">
                  <Filter className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground font-medium">Filters</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {filterableFields.filter(f => allFields[f]).map(fk => (
                    <div key={fk} className="flex items-center gap-1.5">
                      <Label className="text-xs whitespace-nowrap">{allFields[fk]?.label}</Label>
                      <Input
                        value={filters[fk] || ''}
                        onChange={e => {
                          const v = e.target.value;
                          setFilters(prev => v ? { ...prev, [fk]: v } : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== fk)));
                        }}
                        placeholder="Any"
                        className="h-7 w-32 text-xs"
                        data-testid={`filter-${fk}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Bar */}
            <div className="p-3 border-b bg-card flex items-center gap-3">
              <Button onClick={() => { setPage(0); runReport(0); }} disabled={columns.length === 0 || loading} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="run-report-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                Run Report
              </Button>
              <div className="h-6 w-px bg-border" />
              <Button variant="outline" size="sm" onClick={() => handleExport('csv')} disabled={rows.length === 0 || !!exporting} data-testid="export-csv-btn">
                {exporting === 'csv' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={rows.length === 0 || !!exporting} data-testid="export-excel-btn">
                {exporting === 'excel' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                Excel
              </Button>
              <div className="h-6 w-px bg-border" />
              {/* View Mode Toggle */}
              <div className="flex items-center border rounded-md overflow-hidden">
                <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${viewMode === 'table' ? 'bg-[hsl(var(--brand))] text-white' : 'hover:bg-accent'}`} data-testid="view-table-btn">
                  <TableIcon className="w-3.5 h-3.5" />Table
                </button>
                <button onClick={() => setViewMode('chart')} className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${viewMode === 'chart' ? 'bg-[hsl(var(--brand))] text-white' : 'hover:bg-accent'}`} data-testid="view-chart-btn">
                  <PieChart className="w-3.5 h-3.5" />Chart
                </button>
              </div>
              <div className="flex-1" />
              {total > 0 && (
                <span className="text-xs text-muted-foreground" data-testid="result-count">
                  {total} result{total !== 1 ? 's' : ''}{total > PAGE_SIZE ? ` (page ${page + 1} of ${totalPages})` : ''}
                </span>
              )}
            </div>

            {/* Chart Config Bar (shown in chart mode) */}
            {viewMode === 'chart' && rows.length > 0 && (
              <div className="p-3 border-b bg-card/30 flex flex-wrap items-end gap-3" data-testid="chart-config">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Chart Type</Label>
                  <Select value={chartType} onValueChange={setChartType}>
                    <SelectTrigger className="w-32 h-8" data-testid="chart-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bar">Bar Chart</SelectItem>
                      <SelectItem value="line">Line Chart</SelectItem>
                      <SelectItem value="area">Area Chart</SelectItem>
                      <SelectItem value="pie">Pie Chart</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">X-Axis (Group By)</Label>
                  <Select value={chartXField} onValueChange={setChartXField}>
                    <SelectTrigger className="w-44 h-8" data-testid="chart-x-select"><SelectValue placeholder="Select field" /></SelectTrigger>
                    <SelectContent>
                      {columns.filter(c => allFields[c]?.type === 'string' || allFields[c]?.type === 'boolean').map(c => (
                        <SelectItem key={c} value={c}>{allFields[c]?.label || c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Y-Axis (Value)</Label>
                  <Select value={chartYField} onValueChange={setChartYField}>
                    <SelectTrigger className="w-44 h-8" data-testid="chart-y-select"><SelectValue placeholder="Count (default)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_count">Count</SelectItem>
                      {columns.filter(c => allFields[c]?.type === 'number').map(c => (
                        <SelectItem key={c} value={c}>{allFields[c]?.label || c} (sum)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Results Area */}
            <div className="flex-1 overflow-auto">
              {rows.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
                  <Database className="w-12 h-12 opacity-20" />
                  <p className="text-sm">Select columns and click "Run Report" to see results</p>
                </div>
              ) : viewMode === 'chart' ? (
                <ReportChart rows={rows} columns={columns} allFields={allFields} chartType={chartType} xField={chartXField} yField={chartYField} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map(col => (
                        <TableHead key={col} className="whitespace-nowrap cursor-pointer hover:text-foreground transition-colors" onClick={() => handleSort(col)}>
                          <div className="flex items-center gap-1">
                            {allFields[col]?.label || col}
                            {sortField === col && <ArrowUpDown className="w-3 h-3 text-[hsl(var(--brand))]" />}
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="text-center py-8">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : rows.map((row, i) => (
                      <TableRow key={i} data-testid={`report-row-${i}`}>
                        {columns.map(col => (
                          <TableCell key={col} className="text-sm font-mono whitespace-nowrap">
                            {formatCell(row[col], allFields[col]?.type)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-3 border-t bg-card flex items-center justify-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => handlePageChange(page - 1)} data-testid="prev-page">Previous</Button>
                <span className="text-xs text-muted-foreground">Page {page + 1} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)} data-testid="next-page">Next</Button>
              </div>
            )}
          </div>
        </div>

        {/* Save Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="max-w-sm" data-testid="save-report-dialog">
            <DialogHeader>
              <DialogTitle>{editingReportId ? 'Update Report' : 'Save Report'}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label className="mb-2 block">Report Name</Label>
              <Input value={reportName} onChange={e => setReportName(e.target.value)} placeholder="e.g. Monthly Sessions Summary" data-testid="report-name-input" autoFocus />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={!reportName.trim()} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="confirm-save-btn">
                <Save className="w-4 h-4 mr-1" />{editingReportId ? 'Update' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Load Dialog */}
        <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
          <DialogContent className="max-w-lg" data-testid="load-report-dialog">
            <DialogHeader>
              <DialogTitle>Saved Reports</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[400px]">
              {savedReports.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No saved reports yet</p>
              ) : (
                <div className="space-y-2">
                  {savedReports.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors" data-testid={`saved-report-${r.id}`}>
                      <div className="flex-1 cursor-pointer" onClick={() => handleLoad(r)}>
                        <p className="font-medium text-sm">{r.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-[10px]">{schema[r.source]?.label || r.source}</Badge>
                          <span className="text-[10px] text-muted-foreground">{r.columns?.length || 0} columns</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(r.id)} data-testid={`delete-report-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

function formatCell(value, type) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  if (type === 'boolean') return value ? <Badge className="text-[10px] bg-green-500/10 text-green-500">Yes</Badge> : <Badge variant="outline" className="text-[10px]">No</Badge>;
  if (type === 'date' && typeof value === 'string') {
    try { return new Date(value).toLocaleString(); } catch { return String(value); }
  }
  if (type === 'number') return <span className="tabular-nums">{value}</span>;
  return String(value);
}

// ─── Chart Component ───
function ReportChart({ rows, columns, allFields, chartType, xField, yField }) {
  if (!xField) {
    // Auto-pick first string column as X
    const firstStr = columns.find(c => allFields[c]?.type === 'string');
    if (firstStr) xField = firstStr;
    else return <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Select an X-Axis field to generate a chart</div>;
  }

  const yKey = yField || '_count';

  // Aggregate data: group by xField, sum yField or count
  const groups = {};
  rows.forEach(row => {
    const key = String(row[xField] ?? 'Unknown');
    if (!groups[key]) groups[key] = { name: key, _count: 0 };
    groups[key]._count += 1;
    if (yKey !== '_count' && row[yKey] !== undefined) {
      groups[key][yKey] = (groups[key][yKey] || 0) + (Number(row[yKey]) || 0);
    }
  });
  const chartData = Object.values(groups).sort((a, b) => (b[yKey] || b._count) - (a[yKey] || a._count)).slice(0, 20);
  const valueKey = yKey;
  const valueLabel = yKey === '_count' ? 'Count' : (allFields[yKey]?.label || yKey);

  const tooltipStyle = { contentStyle: { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' } };

  if (chartType === 'pie') {
    return (
      <div className="p-6 h-full flex flex-col" data-testid="chart-container">
        <p className="text-sm font-medium mb-4 text-center">{allFields[xField]?.label || xField} — {valueLabel}</p>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPie>
            <Pie data={chartData} dataKey={valueKey} nameKey="name" cx="50%" cy="50%" outerRadius="80%" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
              {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip {...tooltipStyle} />
            <Legend />
          </RechartsPie>
        </ResponsiveContainer>
      </div>
    );
  }

  const ChartComp = chartType === 'line' ? LineChart : chartType === 'area' ? AreaChart : BarChart;
  const DataComp = chartType === 'line' ? Line : chartType === 'area' ? Area : Bar;
  const extraProps = chartType === 'area' ? { fillOpacity: 0.3 } : {};

  return (
    <div className="p-6 h-full flex flex-col" data-testid="chart-container">
      <p className="text-sm font-medium mb-4">{allFields[xField]?.label || xField} — {valueLabel}</p>
      <ResponsiveContainer width="100%" height="100%">
        <ChartComp data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip {...tooltipStyle} />
          <Legend />
          <DataComp dataKey={valueKey} name={valueLabel} fill={CHART_COLORS[0]} stroke={CHART_COLORS[0]} {...extraProps} />
        </ChartComp>
      </ResponsiveContainer>
    </div>
  );
}
